import { DataRefreshEvent } from "@fp/admin-api-client";
import { Button } from "@fp/ui/components/base/buttons/button";
import {
  PageContentContainer,
  PageLoading,
} from "@fp/ui/components/base/page-layout/page-layout";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { toast } from "sonner";
import axios, { getErrorMessage } from "@/lib/axios";
import { useDataRefresh } from "@/hooks/useSignalR";
import { AdminLayout } from "@/components/admin-layout";
import { AdminPageHeader } from "@/components/admin-page-header";
import { useUserContext } from "@/components/user-context";
import { ConfirmationModal } from "@fp/ui/components/application/modals/confirmation-modal";
import { AddButton } from "@fp/ui/components/base/buttons/add-button";
import { OrganizationAvatar } from "@fp/ui/components/base/avatar/organization-avatar";
import { formatLocalDate } from "@fp/ui/components/common/local-date-time";

interface OrganizationInvitationDto {
  id: string;
  organizationId: string;
  organizationName: string;
  email: string;
  role: string;
  invitationType: string;
  isActive: boolean;
  isAccepted: boolean;
  acceptedDate: string | null;
  isDeclined: boolean;
  declinedDate: string | null;
  expiryDate: string;
  lastSentDate: string;
  createdDate: string;
}

interface InvitationDetailsDto {
  id: string;
  organizationId: string;
  organizationName: string;
  organizationLogoUrl: string | null;
  email: string;
  role: string;
  invitationType: string;
  expiryDate: string;
  invitedByUserFullName: string | null;
}

interface ReceivedInvitationsResponse {
  items: InvitationDetailsDto[];
  count: number;
}

const ROLE_LABELS: Record<string, string> = {
  ORG_OWNER: "Owner",
  ORG_ADMIN: "Admin",
  ORG_MANAGER: "Manager",
  ORG_PAYOPS: "Payment Ops",
  ORG_TALENT: "Talent Member",
};

export const Route = createFileRoute("/invitations/")({
  component: RouteComponent,
  pendingComponent: () => <PageLoading />,
});

function RouteComponent() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { refetchOrganizations } = useUserContext();

  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [acceptingId, setAcceptingId] = useState<string | null>(null);
  const [decliningId, setDecliningId] = useState<string | null>(null);
  const [dismissingId, setDismissingId] = useState<string | null>(null);
  const [isDismissingAll, setIsDismissingAll] = useState(false);
  const [showDismissAllModal, setShowDismissAllModal] = useState(false);
  const [cancelModalInvitation, setCancelModalInvitation] = useState<{ id: string; email: string } | null>(null);
  const [declineModalInvitation, setDeclineModalInvitation] = useState<{ id: string; orgName: string } | null>(null);

  const { data: sentInvitations, isLoading: isLoadingSent } = useQuery({
    queryKey: ["sent-invitations"],
    queryFn: async () => {
      const response = await axios.get<OrganizationInvitationDto[]>("/api/sent-invitations");
      return response.data;
    },
  });

  const { data: receivedResponse, isLoading: isLoadingReceived } = useQuery({
    queryKey: ["received-invitations"],
    queryFn: async () => {
      const result = await axios.get<ReceivedInvitationsResponse>("/api/received-invitations");
      return result.data;
    },
  });

  const receivedInvitations = receivedResponse?.items;

  const invalidateInvitations = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["sent-invitations"] });
    queryClient.invalidateQueries({ queryKey: ["received-invitations"] });
  }, [queryClient]);

  useDataRefresh(DataRefreshEvent.InvitationCreated, invalidateInvitations);
  useDataRefresh(DataRefreshEvent.InvitationAccepted, invalidateInvitations);

  const cancelMutation = useMutation({
    mutationFn: async (id: string) => {
      await axios.delete(`/api/sent-invitations/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sent-invitations"] });
      setCancellingId(null);
    },
    onError: (err) => {
      toast.error(getErrorMessage(err, "Failed to cancel invitation. Please try again."));
      setCancellingId(null);
    },
  });

  const resendMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await axios.post<OrganizationInvitationDto>(`/api/sent-invitations/${id}/resend`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sent-invitations"] });
      setResendingId(null);
    },
    onError: (err) => {
      toast.error(getErrorMessage(err, "Failed to resend invitation. Please try again."));
      setResendingId(null);
    },
  });

  const acceptMutation = useMutation({
    mutationFn: async (id: string) => {
      await axios.post(`/api/received-invitations/${id}/accept`);
    },
    onSuccess: async () => {
      await refetchOrganizations();
      queryClient.invalidateQueries({ queryKey: ["received-invitations"] });
      setAcceptingId(null);
    },
    onError: (err) => {
      toast.error(getErrorMessage(err, "Failed to accept invitation. Please try again."));
      setAcceptingId(null);
    },
  });

  const declineMutation = useMutation({
    mutationFn: async (id: string) => {
      await axios.post(`/api/received-invitations/${id}/decline`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["received-invitations"] });
      setDecliningId(null);
    },
    onError: (err) => {
      toast.error(getErrorMessage(err, "Failed to decline invitation. Please try again."));
      setDecliningId(null);
    },
  });

  const dismissMutation = useMutation({
    mutationFn: async (id: string) => {
      await axios.post(`/api/sent-invitations/${id}/dismiss`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sent-invitations"] });
      setDismissingId(null);
    },
    onError: (err) => {
      toast.error(getErrorMessage(err, "Failed to dismiss invitation. Please try again."));
      setDismissingId(null);
    },
  });

  const dismissAllMutation = useMutation({
    mutationFn: async () => {
      const response = await axios.post<{ dismissedCount: number }>("/api/sent-invitations/dismiss-all");
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sent-invitations"] });
      setIsDismissingAll(false);
      setShowDismissAllModal(false);
    },
    onError: (err) => {
      toast.error(getErrorMessage(err, "Failed to dismiss invitations. Please try again."));
      setIsDismissingAll(false);
    },
  });

  const handleCancel = (id: string, email: string) => {
    setCancelModalInvitation({ id, email });
  };

  const handleCancelConfirm = () => {
    if (cancelModalInvitation) {
      setCancellingId(cancelModalInvitation.id);
      cancelMutation.mutate(cancelModalInvitation.id);
      setCancelModalInvitation(null);
    }
  };

  const handleResend = (id: string) => {
    setResendingId(id);
    resendMutation.mutate(id);
  };

  const handleAccept = (id: string) => {
    setAcceptingId(id);
    acceptMutation.mutate(id);
  };

  const handleDecline = (id: string, orgName: string) => {
    setDeclineModalInvitation({ id, orgName });
  };

  const handleDeclineConfirm = () => {
    if (declineModalInvitation) {
      setDecliningId(declineModalInvitation.id);
      declineMutation.mutate(declineModalInvitation.id);
      setDeclineModalInvitation(null);
    }
  };

  const handleDismiss = (id: string) => {
    setDismissingId(id);
    dismissMutation.mutate(id);
  };

  const handleDismissAllConfirm = () => {
    setIsDismissingAll(true);
    dismissAllMutation.mutate();
  };

  const isExpired = (expiryDate: string) => {
    return new Date(expiryDate) < new Date();
  };

  const isLoading = isLoadingSent || isLoadingReceived;
  const hasReceivedInvitations = receivedInvitations && receivedInvitations.length > 0;
  const hasSentInvitations = sentInvitations && sentInvitations.length > 0;
  const hasCompletedInvitations = sentInvitations?.some(i => i.isAccepted || i.isDeclined) ?? false;

  return (
    <AdminLayout>
      <AdminPageHeader
        title="Invitations"
        parent={{ url: "/dashboard", label: "Home" }}
      />
      <PageContentContainer className="space-y-6">
        {isLoading ? (
          <PageLoading />
        ) : (
          <>
            {/* Received Invitations Section */}
            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Received</h2>
              {hasReceivedInvitations ? (
                <div className="space-y-3">
                  {receivedInvitations.map((invitation) => (
                    <div
                      key={invitation.id}
                      className="bg-white border border-gray-200 rounded-lg p-4 space-y-3"
                    >
                      <div className="flex items-start gap-3">
                        <OrganizationAvatar
                          name={invitation.organizationName}
                          logoUrl={invitation.organizationLogoUrl}
                          size="md"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-gray-900">
                            {invitation.organizationName}
                          </div>
                          <div className="text-sm text-gray-500">
                            {invitation.invitationType === "Staff" ? "Staff" : "Talent Member"} Invitation
                          </div>
                        </div>
                      </div>
                      {invitation.invitedByUserFullName && (
                        <div className="text-sm text-gray-500">
                          Invited by {invitation.invitedByUserFullName}
                        </div>
                      )}
                      <div className="text-sm text-gray-500">
                        Sent to {invitation.email}
                      </div>
                      <div className="text-sm text-gray-600">
                        Expires: {formatLocalDate(invitation.expiryDate, "dateWithYear")}
                      </div>
                      <div className="flex justify-between pt-3">
                        <Button
                          color="secondary"
                          onClick={() => handleDecline(invitation.id, invitation.organizationName)}
                          isLoading={decliningId === invitation.id}
                          isDisabled={acceptingId === invitation.id || decliningId === invitation.id}
                        >
                          Decline
                        </Button>
                        <Button
                          color="primary"
                          onClick={() => handleAccept(invitation.id)}
                          isLoading={acceptingId === invitation.id}
                          isDisabled={acceptingId === invitation.id || decliningId === invitation.id}
                        >
                          Accept
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No pending invitations to join organizations.</p>
              )}
            </section>

            <hr className="border-gray-200" />

            {/* Sent Invitations Section */}
            <section>
              <div className="flex justify-between items-center mb-3">
                <h2 className="text-lg font-semibold text-gray-900">Sent</h2>
                <div className="flex items-center gap-2">
                  {hasCompletedInvitations && (
                    <Button
                      color="secondary"
                      onClick={() => setShowDismissAllModal(true)}
                    >
                      Dismiss All
                    </Button>
                  )}
                  <AddButton onClick={() => navigate({ to: "/invitations/new" })}>
                    New
                  </AddButton>
                </div>
              </div>
              {hasSentInvitations ? (
                <div className="space-y-3">
                  {sentInvitations.map((invitation) => {
                    const expired = isExpired(invitation.expiryDate);
                    const isPending = !invitation.isAccepted && !invitation.isDeclined && invitation.isActive;
                    const isCompleted = invitation.isAccepted || invitation.isDeclined;
                    return (
                      <div
                        key={invitation.id}
                        className="bg-white border border-gray-200 rounded-lg p-4"
                      >
                        <div className="flex justify-between items-start gap-4">
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-gray-900 truncate">
                              {invitation.email}
                            </div>
                            <div className="flex flex-wrap gap-2 mt-1">
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                {ROLE_LABELS[invitation.role] || invitation.role}
                              </span>
                              {invitation.isAccepted && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                  Accepted
                                </span>
                              )}
                              {invitation.isDeclined && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                                  Declined
                                </span>
                              )}
                              {isPending && expired && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                  Expired
                                </span>
                              )}
                            </div>
                          </div>
                          {isCompleted && (
                            <Button
                              color="secondary"
                              size="sm"
                              onClick={() => handleDismiss(invitation.id)}
                              isLoading={dismissingId === invitation.id}
                              isDisabled={dismissingId === invitation.id}
                            >
                              Dismiss
                            </Button>
                          )}
                        </div>
                        <div className="text-sm text-gray-600 mt-3 grid grid-cols-[auto_1fr] gap-x-2">
                          <span>Sent:</span>
                          <span>{formatLocalDate(invitation.lastSentDate, "datetimeWithYear")}</span>
                          {invitation.isAccepted && invitation.acceptedDate && (
                            <>
                              <span>Accepted:</span>
                              <span>{formatLocalDate(invitation.acceptedDate, "datetimeWithYear")}</span>
                            </>
                          )}
                          {invitation.isDeclined && invitation.declinedDate && (
                            <>
                              <span>Declined:</span>
                              <span>{formatLocalDate(invitation.declinedDate, "datetimeWithYear")}</span>
                            </>
                          )}
                          {isPending && (
                            <>
                              <span>Expires:</span>
                              <span>{formatLocalDate(invitation.expiryDate, "datetimeWithYear")}</span>
                            </>
                          )}
                        </div>
                        {isPending && (
                          <div className="flex gap-3 pt-3 mt-3 border-t border-gray-100">
                            <Button
                              color="secondary-destructive"
                              onClick={() => handleCancel(invitation.id, invitation.email)}
                              isLoading={cancellingId === invitation.id}
                              isDisabled={resendingId === invitation.id || cancellingId === invitation.id}
                              className="flex-1"
                            >
                              Cancel
                            </Button>
                            <Button
                              color="secondary"
                              onClick={() => handleResend(invitation.id)}
                              isLoading={resendingId === invitation.id}
                              isDisabled={resendingId === invitation.id || cancellingId === invitation.id}
                              className="flex-1"
                            >
                              Resend
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-6">
                  <p className="text-sm text-gray-500 mb-3">
                    No pending invitations sent.
                  </p>
                  <Button
                    color="primary"
                    onClick={() => navigate({ to: "/invitations/new" })}
                  >
                    Send Invitation
                  </Button>
                </div>
              )}
            </section>
          </>
        )}
      </PageContentContainer>
      <ConfirmationModal
        isOpen={showDismissAllModal}
        onClose={() => setShowDismissAllModal(false)}
        onConfirm={handleDismissAllConfirm}
        title="Dismiss All"
        message="This will dismiss all accepted and declined invitations from your sent list. This action cannot be undone."
        confirmText="Dismiss All"
        cancelText="Cancel"
        isLoading={isDismissingAll}
      />
      <ConfirmationModal
        isOpen={!!cancelModalInvitation}
        onClose={() => setCancelModalInvitation(null)}
        onConfirm={handleCancelConfirm}
        title="Cancel Invitation"
        message={`Are you sure you want to cancel the invitation to ${cancelModalInvitation?.email}?`}
        confirmText="Cancel Invitation"
        cancelText="Keep"
        isLoading={!!cancellingId}
      />
      <ConfirmationModal
        isOpen={!!declineModalInvitation}
        onClose={() => setDeclineModalInvitation(null)}
        onConfirm={handleDeclineConfirm}
        title="Decline Invitation"
        message={`Decline invitation to join ${declineModalInvitation?.orgName}? This cannot be undone.`}
        confirmText="Decline"
        cancelText="Cancel"
        isLoading={!!decliningId}
      />
    </AdminLayout>
  );
}
